import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ColorPalette } from "@/components/color-palette/ColorPalette";
import {
  AlignDropdown,
  type Alignment,
} from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";
import { TableCellBackgroundDropdown } from "@/components/doc/right-sidebar/table-style/TableCellBackgroundDropdown";
import { getMixedBackgroundClassNames } from "@/components/doc/right-sidebar/table-style/utils";
import { FontSizeDropdown } from "@/components/doc/right-sidebar/text-style/FontSizeDropdown";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { getHeaderStyles } from "@/components/tiptap/tables/getHeaderStyles";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";
import { twMerge } from "@/utils/twMerge";

import { TableCellAlignmentDropdown } from "./TableCellAlignmentDropdown";

import type { HeaderType } from "@/components/tiptap/tables/commands";
import type { CellVerticalAlignment } from "@/components/tiptap/tables/TableCellVerticalAlignment";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FontSize, Color } from "@corca/data";
import type { NodeWithPos } from "@tiptap/core";
import type { PropsWithChildren } from "react";

type Props = {
  doc: DocumentModel;
  tableNode: NodeWithPos;
  type: HeaderType;
};

export const TableHeaderStyle = observer(function TableHeaderStyle({
  doc,
  tableNode,
  type,
}: PropsWithChildren<Props>) {
  const t = useTranslations("sidebar");
  const { tiptap } = doc.editor;

  if (!tiptap) {
    return null;
  }

  const {
    size,
    color: previewTextColors,
    backgroundColor: previewBgColors,
    alignment,
    verticalAlignment,
  } = getHeaderStyles(tableNode.node, type) || {};

  const textColor = previewTextColors?.length === 1 ? previewTextColors[0] : null;
  const bgColor = previewBgColors?.length === 1 ? previewBgColors[0] : null;
  const i18nKey = type === "row" ? "headerRow" : "headerColumn";

  const handleFontSizeChange = (fontSize: FontSize) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.commands.setHeaderFontSize(fontSize, type);
    });
  };

  const handleColorChange = (color: Color) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.commands.setHeaderFontColor(color, type);
    });
  };

  const handleBackgroundColorChange = (backgroundColor: Color) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.commands.setHeaderAttrs({ backgroundColor }, type);
    });
  };

  const handleTextAlignmentChange = (align: Alignment) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.commands.setHeaderTextAlignment(align, type);
    });
  };

  const handleCellAlignmentChange = (verticalAlign: CellVerticalAlignment) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.commands.setHeaderAttrs({ verticalAlignment: verticalAlign }, type);
    });
  };

  const gradientClassNames = twMerge(
    type === "row" && "bg-gradient-to-r",
    type === "column" && "bg-gradient-to-b",
  );

  const previeTextClassNames = twMerge(
    previewTextColors && getMixedBackgroundClassNames(previewTextColors, gradientClassNames),
  );

  const previewBgClassNames = twMerge(
    previewBgColors && getMixedBackgroundClassNames(previewBgColors, gradientClassNames),
  );

  return (
    <SidebarToolbar label={t("toolbars.tableHeader")} className="flex-1 -mt-1.25">
      <FontSizeDropdown
        tooltipTitle={t(`tableStyle.${i18nKey}.fontSize`)}
        fontSize={size}
        onChange={handleFontSizeChange}
      />
      <ColorPalette
        tooltipTitle={t(`tableStyle.${i18nKey}.color`)}
        currentColor={textColor}
        onSelect={handleColorChange}
        icon={
          <div
            className={twMerge(
              "flex items-center w-full h-full mx-[7px] rounded-full text-transparent bg-clip-text",
              !previewTextColors && "text-document-text",
              previeTextClassNames,
            )}
          >
            A
          </div>
        }
      />
      <TableCellBackgroundDropdown
        tooltipTitle={t(`tableStyle.${i18nKey}.backgroundColor`)}
        currentColor={bgColor}
        onSelect={handleBackgroundColorChange}
        iconClassName={previewBgClassNames}
      />
      <AlignDropdown
        tooltipTitle={t(`tableStyle.${i18nKey}.textAlign`)}
        currentAlignment={alignment}
        onSelect={handleTextAlignmentChange}
      />
      <TableCellAlignmentDropdown
        tooltipTitle={t(`tableStyle.${i18nKey}.verticalTextAlign`)}
        alignment={verticalAlignment}
        onSelect={handleCellAlignmentChange}
      />
    </SidebarToolbar>
  );
});
